// Query-key factory + queryOptions for the admin SPA. One place so keys stay
// consistent between the components that read a query and the mutations that
// invalidate it. All fetchers throw `ApiError` (see lib/api.ts); components read
// `query.error` and branch on `ApiError.status` (401/403/501).
import { type BudgetRange, EMPTY_BUDGET_RANGE, budgetKey } from "./budget-range";
import type {
  TalentQuerySelection,
  AbPresetWindow,
  CohortWindow,
  FunnelPresetWindow,
  LaunchDrilldownMetric,
  LaunchDrilldownWindow,
  TimeframeWindow,
} from "@recruiter/shared/types";
import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";

import { EMPTY_DATE_RANGE } from "@recruiter/shared";

import { type DateRange, dateRangeKey } from "./date-range";
import type { ExperimentScope } from "./experiment-scope";
import {
  ApiError,
  fetchAdminExperiments,
  fetchAllocation,
  fetchExperimentation,
  fetchExperimentationLifecycle,
  fetchExperimentationTriggers,
  fetchExperimentIssues,
  fetchExperimentDetail,
  fetchGuardrailThresholds,
  fetchArmProjects,
  fetchProjectArms,
  fetchTagVersions,
  fetchInjectionDials,
  fetchProjectExperiments,
  type LifecycleFilters,
  fetchAdminBudgetThresholds,
  fetchPromptSectionText,
  fetchPromptSections,
  fetchLaunchDashboardArm,
  fetchAgentRuns,
  fetchAlerts,
  fetchBigClients,
  fetchConversation,
  fetchDebugProjects,
  fetchDebugTurns,
  fetchSavedReplies,
  fetchTurnRuns,
  fetchConversationTranslation,
  fetchExplorerProject,
  type ExplorerProjectFilters,
  type ExplorerUserFilters,
  fetchExplorerProjects,
  fetchExplorerTalent,
  fetchExplorerUser,
  fetchExplorerUsers,
  fetchAdminFunnel,
  fetchAbTests,
  fetchAbTestsRange,
  fetchAdminFunnelRange,
  fetchVersionCohorts,
  fetchVersionCohortsSlice,
  fetchShortlistTimeframe,
  fetchAdminCountries,
  fetchLaunchDashboard,
  fetchLaunchDrilldown,
  fetchMe,
  fetchMyAreas,
  fetchPlatformKillStatus,
  fetchPerson,
  fetchPermissions,
  fetchSupportAttention,
  fetchSupportOverview,
  fetchSupportProjects,
  fetchDeclines,
  fetchOffers,
  fetchSearchDetail,
  fetchSearches,
  fetchSupportStats,
  queryTalentMetrics,
  fetchVipSellers,
  fetchExcluded,
  fetchOptout,
  fetchOptoutRequests,
  fetchSellerPreferences,
  fetchShortlist,
  searchPeople,
} from "./api";

export const adminKeys = {
  explorerUsers: ["admin", "explorer", "users"] as const,
  explorerUser: (id: string) => ["admin", "explorer", "user", id] as const,
  explorerProjects: ["admin", "explorer", "projects"] as const,
  explorerProject: (id: string) => ["admin", "explorer", "project", id] as const,
  conversationTranslation: (projectId: string, key: string) =>
    ["admin", "explorer", "project", projectId, "translation", key] as const,
  shortlist: (id: string) => ["admin", "explorer", "project", id, "shortlist"] as const,
  explorerTalent: (sellerId: string) => ["admin", "explorer", "talent", sellerId] as const,
  alerts: ["admin", "alerts"] as const,
  // The filter is part of the key because it narrows SERVER-side: two
  // selections are two different lists, each with its own keyset cursor, not
  // one list rendered two ways. Extends the base key so the resolve mutations'
  // prefix invalidation still reaches every filtered variant.
  alertsList: (q: string, reasons: string) => ["admin", "alerts", q, reasons] as const,
  bigClients: ["admin", "big-clients"] as const,
  bigClientsList: (q: string, states: string) => ["admin", "big-clients", q, states] as const,
  savedReplies: ["admin", "saved-replies"] as const,
  me: ["admin", "me"] as const,
  myAreas: ["admin", "permissions", "me"] as const,
  permissions: ["admin", "permissions"] as const,
  platformKill: ["admin", "platform-kill"] as const,
  support: ["admin", "support"] as const,
  supportOverview: ["admin", "support", "overview"] as const,
  supportStats: ["admin", "support", "stats"] as const,
  supportAttention: ["admin", "support", "attention"] as const,
  supportProjects: (q: string) => ["admin", "support", "projects", q] as const,
  supportPeople: (q: string) => ["admin", "support", "people", q] as const,
  supportPerson: (id: string) => ["admin", "support", "person", id] as const,
  conversation: (projectId: string) => ["admin", "support", "conversations", projectId] as const,
  // Keyed BY THE SELECTION: a different range, filter, sort or page is a
  // different question with its own answer, so it gets its own cache entry
  // rather than invalidating the previous one. `resultKey` is the server's own
  // content hash of the same selection, so the two agree by construction.
  talentMetrics: (selection: TalentQuerySelection) =>
    ["admin", "talent", "metrics", selection] as const,
  talentSearches: ["admin", "talent", "searches"] as const,
  // "list" keeps the filtered list keys out of `talentSearch(id)`'s namespace;
  // the filter strings are in the key because they narrow server-side.
  talentSearchesList: (filters: string) =>
    ["admin", "talent", "searches", "list", filters] as const,
  talentOffers: ["admin", "talent", "offers"] as const,
  talentOffersList: (filters: string) => ["admin", "talent", "offers", "list", filters] as const,
  talentDeclines: ["admin", "talent", "declines"] as const,
  talentDeclinesList: (filters: string) =>
    ["admin", "talent", "declines", "list", filters] as const,
  talentVip: ["admin", "talent", "vip"] as const,
  talentExcluded: ["admin", "talent", "excluded"] as const,
  talentOptout: ["admin", "talent", "optout"] as const,
  talentOptoutRequests: ["admin", "talent", "optout-requests"] as const,
  sellerPreferences: (status: string) => ["admin", "talent", "seller-preferences", status] as const,
  talentSearch: (id: string) => ["admin", "talent", "searches", id] as const,
  debugProjects: (q: string) => ["admin", "debug", "projects", q] as const,
  // Keyed by the SCOPE, not by a project id: the "No project" bucket is a real
  // scope with no id, and keying on `projectId ?? ""` would collide it with a
  // project whose id is missing. The agent filter is part of the key because it
  // narrows SERVER-side: two selections are two different lists, each with its
  // own cursor, not one list rendered two ways.
  debugTurns: (scope: string, agents: string) =>
    ["admin", "debug", "turns", scope, agents] as const,
  debugTurn: (turnId: string) => ["admin", "debug", "turn", turnId] as const,
  debugAgentFeed: (agent: string) => ["admin", "debug", "agent", agent] as const,
  funnel: (window: string) => ["admin", "funnel", window] as const,
  // The custom range is its own namespace, not a fifth `funnel` window: it is a
  // different route with a different payload shape, and prefix-invalidating the
  // presets should not reach a range somebody is waiting on.
  funnelRange: (range: string, scope: string, devices: string, countries: string, budget: string) =>
    ["admin", "funnel", "range", range, scope, devices, countries, budget] as const,
  // The origin-country vocabulary every country filter lists from. One key: it is
  // the store's whole set, never narrowed.
  countries: ["admin", "countries"] as const,
  // A/B-scoped boards live under their own key prefixes, for `funnelRange`'s
  // reason: a different route, a different payload shape, and invalidating the
  // whole-population board should not reach an arm somebody is waiting on.
  launchArm: (scope: string) => ["admin", "launch", "arm", scope] as const,
  experiments: () => ["admin", "experiments"] as const,
  budgetThresholds: () => ["admin", "budget-thresholds"] as const,
  promptSections: () => ["admin", "prompt-sections"] as const,
  promptSectionText: (key: string) => ["admin", "prompt-section-text", key] as const,
  injectionDials: (key: string) => ["admin", "injection-dials", key] as const,
  experimentation: ["admin", "experimentation"] as const,
  experimentationSnapshot: ["admin", "experimentation", "snapshot"] as const,
  // The filter is part of the key because it narrows SERVER-side.
  experimentationLifecycle: (filters: string) =>
    ["admin", "experimentation", "lifecycle", filters] as const,
  experimentationProject: (projectId: string) =>
    ["admin", "experimentation", "project", projectId] as const,
  // The KIND is part of the key: one declared slot is two experiments (its dial
  // and its approaches), with two allocations under one key, and a shared cache
  // entry would show a section's dial on the approach control.
  experimentationAllocation: (key: string, kind = "") =>
    ["admin", "experimentation", "allocation", key, kind] as const,
  experimentationTriggers: ["admin", "experimentation", "triggers"] as const,
  experimentationIssues: (key: string) => ["admin", "experimentation", "issues", key] as const,
  experimentationGuardrails: (key: string) =>
    ["admin", "experimentation", "guardrails", key] as const,
  // Keyed on kind as well as key: a dialled section that also declares
  // approaches is two experiments under one name, and one cache entry for both
  // would serve whichever was opened first.
  experimentationDetail: (key: string, kind: string) =>
    ["admin", "experimentation", "detail", key, kind] as const,
  experimentationTags: ["admin", "experimentation", "tags"] as const,
  experimentationProjectArms: (projectId: string) =>
    ["admin", "experimentation", "project-arms", projectId] as const,
  // Keyed on (key, arm), not on key: two arms of one experiment are two
  // populations, and a shared key would serve whichever arm opened first.
  experimentationArmProjects: (key: string, arm: string) =>
    ["admin", "experimentation", "arm-projects", key, arm] as const,
  // The custom range is its own namespace, not a fifth `abTests` window: it is
  // computed on demand and polled, and an invalidation sweep over the cheap
  // presets should not reach a range somebody is waiting on. Same split, same
  // reason, as `funnelRange` above.
  abTestsRange: (experiment: string, range: string) =>
    ["admin", "ab-tests", "range", experiment, range] as const,
  abTests: (experiment: string, window: string) =>
    ["admin", "ab-tests", experiment, window] as const,
  // Keyed on BOTH the window and the release, so flipping either refetches
  // through the key rather than reusing a comparison built around a different
  // boundary. `release` is the empty string for "whichever is newest".
  versionCohorts: (window: string, release: string) =>
    ["admin", "version-cohorts", window, release] as const,
  // The device/country SLICE is its own namespace, for `funnelRange`'s reason: a
  // different route with a polled envelope, and invalidating the whole-population
  // read should not reach a cohort somebody is waiting on.
  versionCohortsSlice: (window: string, release: string, cohort: string) =>
    ["admin", "version-cohorts", "slice", window, release, cohort] as const,
  // The shortlist-timeframe readout, keyed on its roll-up window.
  shortlistTimeframe: (window: string) => ["admin", "shortlist-timeframe", window] as const,
  // Keyed by presence-of-token only (never the token value — it would land in
  // devtools and in any serialized cache), so the kiosk and the signed-in view
  // cache separately.
  launchDashboard: (viaToken: boolean) => ["admin", "launch", "dashboard", viaToken] as const,
  launchDrilldown: (metric: string, window: string) =>
    ["admin", "launch", "drilldown", metric, window] as const,
};

export const meQuery = queryOptions({
  queryKey: adminKeys.me,
  queryFn: fetchMe,
  // Identity is stable for the session; don't refetch it on focus.
  staleTime: Infinity,
  retry: false,
});

/** The caller's grants, as (area, level) pairs. ONE source for three questions:
 *  which nav entries exist, which routes `<AreaGate>` opens, and which controls a
 *  page draws (`canWrite`). Never fetch a second copy — a page that reads its own
 *  would be free to disagree with the sidebar about what someone may do. */
export const myAreasQuery = queryOptions({
  queryKey: adminKeys.myAreas,
  queryFn: fetchMyAreas,
  select: (data) => data.areas,
  staleTime: Infinity,
  retry: false,
});

/** Whether the BACKEND on the other end understands access levels — see
 *  `MyAccess.levelsSupported`.
 *
 *  Deliberately the SAME query key and fetcher as `myAreasQuery`, differing only
 *  in `select`: TanStack shares one cache entry and one request between them, so
 *  this is a second view of the boot fetch rather than a second call, and the two
 *  can never answer from different responses. */
export const accessLevelsSupportedQuery = queryOptions({
  queryKey: adminKeys.myAreas,
  queryFn: fetchMyAreas,
  select: (data) => data.levelsSupported,
  staleTime: Infinity,
  retry: false,
});

export const permissionsQuery = queryOptions({
  queryKey: adminKeys.permissions,
  queryFn: fetchPermissions,
});

export const platformKillQuery = queryOptions({
  queryKey: adminKeys.platformKill,
  queryFn: fetchPlatformKillStatus,
  retry: false,
});

/** The Funnel section's snapshot, one cohort window at a time. Read-mostly, and
 *  now a pure cache read: the WORKER recomputes all four windows every
 *  `REFRESH_PERIOD_SECONDS` (5 min, backend/recruiter/funnel_snapshot.py) and
 *  the route serves what it wrote. So no polling, and a staleTime that MATCHES
 *  that cadence rather than undercutting it — refetching sooner can only
 *  re-read bytes the server has not changed. The page shows `generatedAt` so
 *  the age is disclosed rather than implied. Switching windows fetches through
 *  the key. */
export function funnelQuery(window: FunnelPresetWindow) {
  return queryOptions({
    queryKey: adminKeys.funnel(window),
    queryFn: () => fetchAdminFunnel(window),
    staleTime: 5 * 60 * 1000,
    // Switching windows keeps the previous chart on screen (dimmed) instead of
    // flashing a skeleton; the refetch swaps the numbers in place.
    placeholderData: keepPreviousData,
  });
}

/** How long the Funnel's custom-range view waits for the worker before it stops
 *  asking. The aggregate takes ~10-20s, so two minutes is many times its normal
 *  run; past that something is wrong and continuing to ask cannot fix it.
 *
 *  The bound is not decoration. This console has exactly two other timer-driven
 *  reads and both had to argue for themselves, because a timer nobody switches
 *  off is how an unattended tab once made 5.4% of all platform traffic. This one
 *  answers the same question three ways: it stops on `ready` (the work is done),
 *  it stops on a 401/403 (nothing about asking again makes a dead credential
 *  live), and it stops here (the work is not coming). The page then says so and
 *  offers a retry, rather than spinning in silence. */
export const FUNNEL_RANGE_POLL_MS = 3000;
export const FUNNEL_RANGE_MAX_POLLS = Math.ceil((2 * 60 * 1000) / FUNNEL_RANGE_POLL_MS);

/** The Funnel over a CUSTOM cohort range: computed on demand, so this is the one
 *  funnel read that can come back with no funnel in it.
 *
 *  The four presets are materialized together by a worker tick and read straight
 *  from cache (`funnelQuery` above). A range nobody could precompute is instead
 *  computed when asked for: the route answers `computing` and enqueues, and this
 *  polls until `ready`. That is a BOUNDED, self-terminating poll — it turns
 *  itself off three separate ways (see `FUNNEL_RANGE_MAX_POLLS`) — not a status
 *  loop that outlives its work, which is the only shape this console allows.
 *
 *  Keyed on the range, so flipping between two ranges reads each through its own
 *  cache entry rather than showing one under the other's heading. */
export function funnelRangeQuery(
  range: DateRange,
  scope?: ExperimentScope,
  devices: readonly string[] = [],
  countries: readonly string[] = [],
  budget: BudgetRange = EMPTY_BUDGET_RANGE,
) {
  return queryOptions({
    // The A/B group is part of the KEY, not a view on the same entry: the same
    // dates read for two arms are two different funnels, and sharing a key
    // would show whichever arrived first under both headings. The DEVICE
    // selection is part of it for the same reason, and sorted for the same
    // reason the request sorts it.
    queryKey: adminKeys.funnelRange(
      dateRangeKey(range),
      scope ? `${scope.experiment}:${scope.arm}` : "-",
      [...devices].sort().join("+") || "-",
      [...countries].sort().join("+") || "-",
      // The committed-budget bound is part of the identity for the same reason.
      budgetKey(budget),
    ),
    queryFn: () => fetchAdminFunnelRange(range, scope, devices, countries, budget),
    refetchInterval: (query) => {
      if (isAuthFailure(query.state.error)) return false;
      if (query.state.data?.status === "ready") return false;
      // `dataUpdateCount` counts responses this query has received, which is the
      // number of times we have asked and been told `computing`.
      if (query.state.dataUpdateCount >= FUNNEL_RANGE_MAX_POLLS) return false;
      return FUNNEL_RANGE_POLL_MS;
    },
    // A range that is still computing must not be re-asked from scratch every
    // time the page remounts; the cached `computing` is a true answer.
    staleTime: FUNNEL_RANGE_POLL_MS,
    placeholderData: keepPreviousData,
  });
}

/** Whether a range view has given up waiting: the poll hit its bound and the
 *  answer is still `computing`. Pure, so the page can say "still working" and
 *  offer a retry rather than spinning forever with no explanation. */
export function funnelRangeGaveUp(status: string | undefined, dataUpdateCount: number): boolean {
  return status === "computing" && dataUpdateCount >= FUNNEL_RANGE_MAX_POLLS;
}

/** One experiment's arms across the funnel steps. A pure cache read like the
 *  Funnel presets: the WORKER rebuilds the readout half-hourly
 *  (backend/recruiter/ab_test_snapshot.py) and the route divides counts into
 *  rates, so the staleTime MATCHES that cadence rather than undercutting it. No
 *  polling. The page renders `generatedAt`, so the age is disclosed. */
/** The A/B allocation dials, for the System Admin sliders. Short staleTime:
 *  the page is where an operator checks a slide took, so it must not serve a
 *  minutes-old answer after a write elsewhere. */
export function adminExperimentsQuery() {
  return queryOptions({
    queryKey: adminKeys.experiments(),
    queryFn: fetchAdminExperiments,
    staleTime: 15 * 1000,
  });
}

/** The committed-budget tiers (the human-handoff / Calendly minimum). Same
 *  read-fresh-on-the-server, short-stale-on-the-client shape as the dials. */
export function adminBudgetThresholdsQuery() {
  return queryOptions({
    queryKey: adminKeys.budgetThresholds(),
    queryFn: fetchAdminBudgetThresholds,
    staleTime: 15 * 1000,
  });
}

export function promptSectionsQuery() {
  return queryOptions({
    queryKey: adminKeys.promptSections(),
    queryFn: fetchPromptSections,
    // Same 15s the section dials propagate in, so the console cannot show a
    // value the renderer has already stopped using.
    staleTime: 15 * 1000,
  });
}

/** One injection's three dials, read on demand (the listing carries them too;
 *  this is the per-key route for a surface that needs a fresh read). */
export function injectionDialsQuery(key: string) {
  return queryOptions({
    queryKey: adminKeys.injectionDials(key),
    queryFn: () => fetchInjectionDials(key),
    enabled: key.length > 0,
    staleTime: 15 * 1000,
  });
}

/** The experimentation snapshot. The WORKER rebuilds it on its half-hourly tick,
 *  so the staleTime matches that cadence rather than undercutting it. No polling:
 *  a `computing` state is rendered as such, and the page says so. */
export function experimentationQuery() {
  return queryOptions({
    queryKey: adminKeys.experimentationSnapshot,
    queryFn: fetchExperimentation,
    staleTime: 5 * 60 * 1000,
  });
}

/** The lifecycle ledger. Short staleTime: it is where an operator checks that a
 *  dial move was recorded, so it must not serve a minutes-old answer. */
export function experimentationLifecycleQuery(filters: LifecycleFilters = {}) {
  const key = [filters.entityKind ?? "", filters.entityKey ?? "", filters.limit ?? ""].join("|");
  return queryOptions({
    queryKey: adminKeys.experimentationLifecycle(key),
    queryFn: () => fetchExperimentationLifecycle(filters),
    staleTime: 15 * 1000,
  });
}

/** One project's exposures, approaches and decisions. Enabled only once an id
 *  is submitted, so the lookup box costs nothing until it is used. */
export function projectExperimentsQuery(projectId: string) {
  return queryOptions({
    queryKey: adminKeys.experimentationProject(projectId),
    queryFn: () => fetchProjectExperiments(projectId),
    enabled: projectId.length > 0,
    staleTime: 30 * 1000,
  });
}

/** One experiment's live allocation with its audit half. Short staleTime: the
 *  detail page is where an operator checks a move took. */
export function allocationQuery(key: string, kind = "") {
  return queryOptions({
    queryKey: adminKeys.experimentationAllocation(key, kind),
    queryFn: () => fetchAllocation(key, kind),
    enabled: key.length > 0,
    staleTime: 15 * 1000,
  });
}

/** One experiment's Issues tab. Enabled only when the tab is open: it is two
 *  grouped reads over a day of telemetry and nobody should pay for it while
 *  looking at the allocation. */
export function experimentIssuesQuery(key: string, enabled: boolean) {
  return queryOptions({
    queryKey: adminKeys.experimentationIssues(key),
    queryFn: () => fetchExperimentIssues(key),
    enabled: enabled && key.length > 0,
    staleTime: 60 * 1000,
  });
}

/** One experiment's guardrail overrides beside the house defaults. */
/** One experiment's content, fetched only when a panel is open.
 *
 *  Never folded into the register's snapshot: the prompt tree alone is half a
 *  million characters, and shipping all of it to draw a list of names would be
 *  slow for every reader in order to serve the few who open one. */
export function experimentDetailQuery(key: string, kind?: string) {
  return queryOptions({
    queryKey: adminKeys.experimentationDetail(key, kind ?? ""),
    queryFn: () => fetchExperimentDetail(key, kind),
    enabled: key.length > 0,
    // Declarations move on a deploy, not on a dial, so this is stale far more
    // slowly than the allocation shown beside it.
    staleTime: 5 * 60 * 1000,
  });
}

export function guardrailThresholdsQuery(key: string) {
  return queryOptions({
    queryKey: adminKeys.experimentationGuardrails(key),
    queryFn: () => fetchGuardrailThresholds(key),
    enabled: key.length > 0,
    staleTime: 30 * 1000,
  });
}

/** Every arm one project is in. Enabled only where the strip renders, so a
 *  project page costs nothing extra for a viewer without the area. */
export function projectArmsQuery(projectId: string) {
  return queryOptions({
    queryKey: adminKeys.experimentationProjectArms(projectId),
    queryFn: () => fetchProjectArms(projectId),
    enabled: projectId.length > 0,
    staleTime: 60 * 1000,
  });
}

/** WHICH PROJECTS are in one arm, for any kind.
 *
 *  `enabled` is the load-bearing part: the route goes through `arm_filter`, and for a
 *  user-unit kind that means reading the A/B snapshot, so it is not free. Nothing
 *  fetches until a reader actually opens an arm.
 *
 *  `staleTime` is long because membership is sticky: a project's arm does not move
 *  once assigned, so a minute-old answer is the same answer. */
export function armProjectsQuery(key: string, arm: string, enabled: boolean) {
  return queryOptions({
    queryKey: adminKeys.experimentationArmProjects(key, arm),
    queryFn: () => fetchArmProjects(key, arm),
    enabled: enabled && key.length > 0 && arm.length > 0,
    staleTime: 60 * 1000,
  });
}

/** The tag trust ladder. Short staleTime: it is where an operator checks that a
 *  promotion took, and a sentinel promotion changes what injections can fire. */
export function tagVersionsQuery(enabled = true) {
  return queryOptions({
    queryKey: adminKeys.experimentationTags,
    queryFn: fetchTagVersions,
    enabled,
    staleTime: 30 * 1000,
  });
}

/** The trigger vocabulary. Code-declared, so it changes only with a deploy. */
export function experimentationTriggersQuery() {
  return queryOptions({
    queryKey: adminKeys.experimentationTriggers,
    queryFn: fetchExperimentationTriggers,
    staleTime: 30 * 60 * 1000,
  });
}

/** One section's full text. Enabled only when a section is open, so the listing
 *  costs nothing extra for a reader who never opens one. */
export function promptSectionTextQuery(key: string) {
  return queryOptions({
    queryKey: adminKeys.promptSectionText(key),
    queryFn: () => fetchPromptSectionText(key),
    enabled: key.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function abTestsQuery(experiment: string | undefined, window: AbPresetWindow) {
  return queryOptions({
    queryKey: adminKeys.abTests(experiment ?? "", window),
    queryFn: () => fetchAbTests(experiment, window),
    // A cold cache answers `computing` and enqueues the rebuild, so this polls
    // until it lands — a BOUNDED, self-terminating poll like the funnel range's,
    // not a status loop that outlives its work.
    refetchInterval: (query) => {
      if (isAuthFailure(query.state.error)) return false;
      if (query.state.data?.status === "ready") return false;
      if (query.state.dataUpdateCount >= FUNNEL_RANGE_MAX_POLLS) return false;
      return FUNNEL_RANGE_POLL_MS;
    },
    staleTime: 30 * 60 * 1000,
    // Switching experiment or window keeps the previous numbers on screen
    // (dimmed) instead of flashing a skeleton; the refetch swaps them in place.
    placeholderData: keepPreviousData,
  });
}

/** The A/B readout over a CUSTOM cohort range: computed on demand, so this is
 *  the one A/B read whose answer can be "not yet".
 *
 *  The four presets are materialized together by a worker tick and read from one
 *  cache key (`abTestsQuery` above). A range nobody could precompute is computed
 *  when asked for: the route answers `computing` and enqueues, and this polls
 *  until `ready`. A BOUNDED, self-terminating poll on the same three exits the
 *  funnel range uses (ready, a dead credential, its own attempt cap) - not a
 *  status loop that outlives its work.
 *
 *  Keyed on the range AND the experiment, because the server keys its compute on
 *  both: a readout carries only the selected experiment's KPI vocabulary, so two
 *  tabs over the same dates are two different payloads. */
export function abTestsRangeQuery(experiment: string | undefined, range: DateRange) {
  return queryOptions({
    queryKey: adminKeys.abTestsRange(experiment ?? "", dateRangeKey(range)),
    queryFn: () => fetchAbTestsRange(experiment, range),
    refetchInterval: (query) => {
      if (isAuthFailure(query.state.error)) return false;
      if (query.state.data?.status === "ready") return false;
      // `dataUpdateCount` counts responses this query has received, which is the
      // number of times we have asked and been told `computing`.
      if (query.state.dataUpdateCount >= FUNNEL_RANGE_MAX_POLLS) return false;
      return FUNNEL_RANGE_POLL_MS;
    },
    // A range that is still computing must not be re-asked from scratch every
    // time the page remounts; the cached `computing` is a true answer.
    staleTime: FUNNEL_RANGE_POLL_MS,
    placeholderData: keepPreviousData,
  });
}

/** The Version Cohorts comparison. A pure cache read like the Funnel above: the
 *  WORKER rebuilds the daily series every `REFRESH_PERIOD_SECONDS` (15 min since
 *  2026-09-07, backend/recruiter/version_cohort_snapshot.py) and the route rolls it
 *  up, so the staleTime MATCHES that cadence rather than undercutting it. No polling.
 *  The page renders `generatedAt`, so the age is disclosed rather than implied. */
export function versionCohortsQuery(window: CohortWindow, release?: string) {
  return queryOptions({
    queryKey: adminKeys.versionCohorts(window, release ?? ""),
    queryFn: () => fetchVersionCohorts(window, release),
    staleTime: 15 * 60 * 1000,
    // Switching release or window keeps the previous numbers on screen (dimmed)
    // instead of flashing a skeleton; the refetch swaps them in place.
    placeholderData: keepPreviousData,
  });
}

/** The shortlist-timeframe readout (Monday 3204302464). The cohorts' lane: a
 *  pure cache read of a series the worker rebuilds every fifteen minutes, so the
 *  staleTime matches that cadence and a window switch keeps the previous table
 *  on screen (dimmed) until the new roll-up lands. */
export function shortlistTimeframeQuery(window: TimeframeWindow) {
  return queryOptions({
    queryKey: adminKeys.shortlistTimeframe(window),
    queryFn: () => fetchShortlistTimeframe(window),
    staleTime: 15 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/** The Version Cohorts page narrowed to ONE device/country cohort, built on demand.
 *
 *  Polls exactly like the Funnel's custom range and stops for the same reasons:
 *  ready, an auth failure, or the poll bound. Keyed on the cohort (both axes,
 *  sorted) beside the window and the release: two cohorts are two different pages
 *  with their own worker computes, never one page rendered two ways. */
export function versionCohortsSliceQuery(
  window: CohortWindow,
  release: string | undefined,
  devices: readonly string[],
  countries: readonly string[],
) {
  return queryOptions({
    queryKey: adminKeys.versionCohortsSlice(
      window,
      release ?? "",
      `${selectionKey(devices) || "-"}:${selectionKey(countries) || "-"}`,
    ),
    queryFn: () => fetchVersionCohortsSlice(window, release, devices, countries),
    refetchInterval: (query) => {
      if (isAuthFailure(query.state.error)) return false;
      if (query.state.data?.status === "ready") return false;
      if (query.state.dataUpdateCount >= FUNNEL_RANGE_MAX_POLLS) return false;
      return FUNNEL_RANGE_POLL_MS;
    },
    staleTime: FUNNEL_RANGE_POLL_MS,
    placeholderData: keepPreviousData,
  });
}

/** The origin-country vocabulary the country filters list. The set changes when a
 *  NEW country first arrives, minutes of staleness on a list of options, so it is
 *  read once per session and shared by every filter that renders it. */
export function countriesQuery() {
  return queryOptions({
    queryKey: adminKeys.countries,
    queryFn: fetchAdminCountries,
    staleTime: 10 * 60 * 1000,
  });
}

/** The scope half of an arm-scoped query key. Named for the A/B arm, because
 *  `scopeKey` is already the debug-turns helper's. */
function armKey(scope: ExperimentScope): string {
  return `${scope.experiment}:${scope.arm}`;
}

/** The launch board for one A/B group, built on demand on the Funnel range's polled lane. */
export function launchArmQuery(scope: ExperimentScope) {
  return queryOptions({
    queryKey: adminKeys.launchArm(armKey(scope)),
    queryFn: () => fetchLaunchDashboardArm(scope),
    refetchInterval: (query) => {
      if (isAuthFailure(query.state.error)) return false;
      // A READY scoped board keeps refreshing, unlike the funnel range: this is
      // the live launch board, and its whole job is to be current. The interval
      // is the poll rather than the board's 60s cadence because a rebuild is
      // what a refetch triggers.
      if (query.state.data?.status === "unavailable") return false;
      if (
        query.state.data?.status === "computing" &&
        query.state.dataUpdateCount >= FUNNEL_RANGE_MAX_POLLS
      ) {
        return false;
      }
      return FUNNEL_RANGE_POLL_MS;
    },
    staleTime: FUNNEL_RANGE_POLL_MS,
    placeholderData: keepPreviousData,
  });
}

/** The launch dashboard's poll cadence. Mirrors the WORKER's snapshot refresh
 *  period (`REFRESH_PERIOD_SECONDS`, backend/recruiter/launch_snapshot.py): a
 *  scheduled worker tick rematerializes the snapshot every fifteen minutes (once
 *  a minute until 2026-09-07, when the aggregate had grown to 17% of the prod
 *  database's CPU) and the route is a pure cache read, so polling faster would
 *  only re-read identical data. Change the two together (and the Cloud Scheduler
 *  cron that deploys the backend half). */
export const LAUNCH_POLL_MS = 15 * 60 * 1000;

/** True when a query failed because the CREDENTIAL is dead rather than the
 *  request: 401 (no/expired identity) or 403 (no grant, or a rejected
 *  remote-view token). Nothing about waiting and asking again makes a dead
 *  credential live, so an interval must stop on one and a retry must not spend
 *  attempts on one. */
export function isAuthFailure(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

/** The interval the launch query should run at given its LAST error: the normal
 *  cadence, or OFF for good once the credential is rejected.
 *
 *  The off switch is not a nicety. A TV holding an expired remote-view token,
 *  or a tab left open past an admin session, kept refetching every 15 seconds
 *  with nobody watching: 13,460 failed requests, 5.4% of all traffic, unnoticed
 *  for 13 hours (prod, Aug 2026). The poll exists to keep LIVE numbers honest;
 *  a request that cannot succeed until a human re-authenticates is not that. */
export function launchRefetchInterval(error: unknown): number | false {
  return isAuthFailure(error) ? false : LAUNCH_POLL_MS;
}

/** The launch-day TV snapshot. This is the console's ONE deliberate exception to
 *  the no-polling rule: a kiosk screen has no user session to focus-refetch on
 *  and the payload is a periodic DB aggregate with no event to push, so it
 *  re-reads on a fixed interval, in the background, for as long as the page is
 *  mounted AND the credential behind it is still good (documented in
 *  ARCHITECTURE.md — do not copy this pattern to other surfaces). The worker
 *  rematerializes the snapshot once a minute and this poll rides that cadence,
 *  so however many boards are open, the fleet costs ZERO aggregate runs on the
 *  web service — every poll is a cache read (issue #763).
 *
 *  It is unbounded in TIME but not in FAILURE: an auth rejection stops the timer
 *  (see `launchRefetchInterval`) and is never retried, so an unattended screen
 *  goes quiet instead of hammering a route it can no longer read. Focus refetch
 *  still fires once when a human comes back to the tab, which is how a
 *  re-authenticated session recovers without a reload. */
export function launchDashboardQuery(token?: string) {
  return queryOptions({
    queryKey: adminKeys.launchDashboard(Boolean(token)),
    queryFn: () => fetchLaunchDashboard(token),
    staleTime: 10 * 1000,
    refetchInterval: (query) => launchRefetchInterval(query.state.error),
    refetchIntervalInBackground: true,
    retry: (failureCount, error) => !isAuthFailure(error) && failureCount < 2,
  });
}

/** The rows behind one launch tile. Fetched when the modal OPENS, not polled:
 *  the modal is a moment's inspection, and the 15s cadence above belongs to the
 *  aggregate snapshot alone. A short staleTime keeps window-switching inside
 *  one open modal from refetching the window it just showed. */
export function launchDrilldownQuery(metric: LaunchDrilldownMetric, window: LaunchDrilldownWindow) {
  return queryOptions({
    queryKey: adminKeys.launchDrilldown(metric, window),
    queryFn: () => fetchLaunchDrilldown(metric, window),
    staleTime: 15 * 1000,
    retry: 1,
  });
}

export const supportOverviewQuery = queryOptions({
  queryKey: adminKeys.supportOverview,
  queryFn: fetchSupportOverview,
});

export const supportStatsQuery = queryOptions({
  queryKey: adminKeys.supportStats,
  queryFn: fetchSupportStats,
});

export const supportAttentionQuery = queryOptions({
  queryKey: adminKeys.supportAttention,
  queryFn: fetchSupportAttention,
});

export function supportProjectsQuery(
  q: string,
  range: DateRange = EMPTY_DATE_RANGE,
  scope?: ExperimentScope,
) {
  return infiniteQueryOptions({
    // The A/B group is part of the KEY: the same search filtered to one arm is a
    // different list, and sharing a key would show one under the other's heading.
    queryKey: adminKeys.supportProjects(
      `${q}|${dateRangeKey(range)}|${scope ? `${scope.experiment}:${scope.arm}` : "-"}`,
    ),
    queryFn: ({ pageParam }) => fetchSupportProjects(q, pageParam, range, scope),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

export function shortlistQuery(projectId: string) {
  return queryOptions({
    queryKey: adminKeys.shortlist(projectId),
    queryFn: () => fetchShortlist(projectId),
  });
}

export function supportPeopleQuery(
  q: string,
  range: DateRange = EMPTY_DATE_RANGE,
  scope?: ExperimentScope,
) {
  return queryOptions({
    queryKey: adminKeys.supportPeople(
      `${q}|${dateRangeKey(range)}|${scope ? `${scope.experiment}:${scope.arm}` : "-"}`,
    ),
    queryFn: () => searchPeople(q, range, scope),
  });
}

export function supportPersonQuery(id: string) {
  return queryOptions({
    queryKey: adminKeys.supportPerson(id),
    queryFn: () => fetchPerson(id),
  });
}

export function conversationQuery(projectId: string) {
  return queryOptions({
    queryKey: adminKeys.conversation(projectId),
    queryFn: () => fetchConversation(projectId),
  });
}

// --- Explorer -----------------------------------------------------------------
// Separate cache namespaces from the Customer Support reads of the same people
// and projects: the two areas fetch DIFFERENT shapes from different routes (CS
// gets the narrow talent-free payload), so sharing a key would let one area's
// response be served to the other. Same reasoning as the CS people/person split.

/** The people list, keyset-paged. EVERY filter and the sort are part of the KEY,
 *  because every one of them narrows or orders SERVER-side: a different
 *  selection is a different list with its own cursor, not the same list
 *  re-rendered. (That is also why they cannot be applied in the browser — a
 *  paged list only has the pages it fetched.) */
export function explorerUsersQuery(filters: ExplorerUserFilters = {}) {
  return infiniteQueryOptions({
    queryKey: [...adminKeys.explorerUsers, userFilterKey(filters)],
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      fetchExplorerUsers(pageParam, filters),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/** Every narrowing input as one comparable string. Built explicitly rather than
 *  by stringifying the object, so a key can never depend on property order. */
function userFilterKey(f: ExplorerUserFilters): string {
  return [
    f.q?.trim() ?? "",
    dateRangeKey(f.range ?? EMPTY_DATE_RANGE),
    f.scope ? `${f.scope.experiment}:${f.scope.arm}` : "-",
    f.blocked ?? "-",
    f.guests ?? "-",
    selectionKey(f.tiers ?? []),
    selectionKey(f.countries ?? []),
    f.sort ?? "-",
    f.desc ?? "-",
  ].join("|");
}

export function explorerUserQuery(id: string) {
  return queryOptions({
    queryKey: adminKeys.explorerUser(id),
    queryFn: () => fetchExplorerUser(id),
  });
}

/** The projects list, keyset-paged. Same contract as `explorerUsersQuery` above:
 *  filters and sort narrow server-side and so they key the query. */
export function explorerProjectsQuery(filters: ExplorerProjectFilters = {}) {
  return infiniteQueryOptions({
    queryKey: [...adminKeys.explorerProjects, projectFilterKey(filters)],
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      fetchExplorerProjects(pageParam, filters),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

function projectFilterKey(f: ExplorerProjectFilters): string {
  return [
    f.q?.trim() ?? "",
    dateRangeKey(f.range ?? EMPTY_DATE_RANGE),
    f.scope ? `${f.scope.experiment}:${f.scope.arm}` : "-",
    selectionKey(f.kinds ?? []),
    selectionKey(f.shapes ?? []),
    selectionKey(f.buyerTypes ?? []),
    selectionKey(f.stages ?? []),
    selectionKey(f.handling ?? []),
    selectionKey(f.alerts ?? []),
    selectionKey(f.devices ?? []),
    selectionKey(f.tiers ?? []),
    selectionKey(f.countries ?? []),
    selectionKey(f.meetings ?? []),
    f.starred ?? "-",
    f.sort ?? "-",
    f.desc ?? "-",
  ].join("|");
}

export function explorerProjectQuery(id: string) {
  return queryOptions({
    queryKey: adminKeys.explorerProject(id),
    queryFn: () => fetchExplorerProject(id),
  });
}

/** One translated transcript, keyed by the CONTENT digest the worker minted for
 *  it. Keying on the digest rather than the project is what makes the cache
 *  correct: a transcript that has grown since is a different key, so a stale
 *  overlay can never be served for a conversation that moved on. Server-side the
 *  same digest keys the cached result, so re-reading one costs nothing. */
export function conversationTranslationQuery(projectId: string, key: string) {
  return queryOptions({
    queryKey: adminKeys.conversationTranslation(projectId, key),
    queryFn: () => fetchConversationTranslation(projectId, key),
    // No background refetching: for a given key the payload changes only when WE
    // replace it, which happens on exactly one path — a retry over a degraded
    // result, where the transcript (and so the digest) is unchanged but the
    // worker declined to reuse the entry. That path invalidates this key
    // explicitly (`use-conversation-translation.ts`), so the ceiling here costs
    // nothing and a stale reading is never what an operator is left looking at.
    staleTime: Infinity,
  });
}

/** One talent's operational history. Its own cache namespace, keyed by the
 *  talent's id (their Fiverr username) — the page pairs it with the BI metrics
 *  snapshot, which stays in its own key so the two sources never share a cache
 *  entry any more than they share a definition. */
export function explorerTalentQuery(sellerId: string) {
  return queryOptions({
    queryKey: adminKeys.explorerTalent(sellerId),
    queryFn: () => fetchExplorerTalent(sellerId),
  });
}

/** The raised-flag worklist, keyset-paged. A rep resolves rows here and expects
 *  the list to reflect that, so this one DOES refetch on focus (unlike the
 *  identity/metrics queries) — still no polling: React Query's focus refetch,
 *  nothing timed.
 *
 *  Keyset is what makes that refetch safe on a list somebody is working. An
 *  invalidate re-runs every loaded page, and each page's cursor is recomputed
 *  from the page before it — so a row resolved on page 1 simply disappears
 *  instead of pulling an unread alert back across a page boundary, which is
 *  exactly what an OFFSET pager would do. */
export function alertsQuery(filter: {
  q: string;
  reasons: readonly string[];
  devices: readonly string[];
  countries: readonly string[];
  range: DateRange;
}) {
  return infiniteQueryOptions({
    queryKey: adminKeys.alertsList(
      filter.q,
      `${selectionKey(filter.reasons)}|${selectionKey(filter.devices)}` +
        `|${selectionKey(filter.countries)}|${dateRangeKey(filter.range)}`,
    ),
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      fetchAlerts(pageParam, {
        q: filter.q || undefined,
        reasons: filter.reasons,
        devices: filter.devices,
        countries: filter.countries,
        range: filter.range,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? null,
  });
}

/** Open big-client alerts, keyset-paged. Same reasoning as the worklist above:
 *  an operator acts on these rows and expects the list to reflect it, so a focus
 *  refetch, never a timer. Filters narrow server-side, so they key the query. */
export function bigClientsQuery(filter: {
  q: string;
  states: readonly string[];
  shapes: readonly string[];
  desks: readonly string[];
  meetings: readonly string[];
  devices: readonly string[];
  countries: readonly string[];
  range: DateRange;
}) {
  return infiniteQueryOptions({
    // Every selection is part of the KEY: these narrow server-side, so a new
    // selection is a different list with its own cursor, not the same list
    // filtered again.
    queryKey: adminKeys.bigClientsList(
      filter.q,
      `${selectionKey(filter.states)}|${selectionKey(filter.shapes)}` +
        `|${selectionKey(filter.desks)}|${selectionKey(filter.meetings)}` +
        `|${selectionKey(filter.devices)}|${selectionKey(filter.countries)}` +
        `|${dateRangeKey(filter.range)}`,
    ),
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      fetchBigClients(pageParam, {
        q: filter.q || undefined,
        states: filter.states,
        shapes: filter.shapes,
        desks: filter.desks,
        meetings: filter.meetings,
        devices: filter.devices,
        countries: filter.countries,
        range: filter.range,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? null,
  });
}

/** Shared operator reply snippets. Account-wide and rarely edited, so they are
 *  fetched once per session rather than per conversation. */
export const savedRepliesQuery = queryOptions({
  queryKey: adminKeys.savedReplies,
  queryFn: fetchSavedReplies,
  staleTime: 5 * 60 * 1000,
});

/** One answered Talent SELECTION. The warehouse only moves on its own cadence, so
 *  a refetch on every focus would be wasted work; the UI reads freshness from the
 *  result's own `generatedAt` (warehouse time), never from fetch time.
 *
 *  A `pending` result is a normal, successful response — not an error and not a
 *  retry cue. The page waits for the Pusher event and then reads the answer. */
export const talentMetricsQuery = (selection: TalentQuerySelection) =>
  queryOptions({
    queryKey: adminKeys.talentMetrics(selection),
    queryFn: () => queryTalentMetrics(selection),
    staleTime: 5 * 60 * 1000,
  });

/** Curated lists — operational Postgres, short staleTime (edits are live). */
export const talentVipQuery = queryOptions({
  queryKey: adminKeys.talentVip,
  queryFn: fetchVipSellers,
  staleTime: 30 * 1000,
});
export const talentExcludedQuery = queryOptions({
  queryKey: adminKeys.talentExcluded,
  queryFn: fetchExcluded,
  staleTime: 30 * 1000,
});
export const talentOptoutQuery = queryOptions({
  queryKey: adminKeys.talentOptout,
  queryFn: fetchOptout,
  staleTime: 30 * 1000,
});

/** Recent searches, keyset-paged. Operational data, so it moves with the product
 *  rather than on the warehouse cadence — a short staleTime, not the snapshot's.
 *  Filters narrow server-side across the whole feed, so they key the query. */
export function talentSearchesQuery(filter: {
  surfaces: readonly string[];
  outcomes: readonly string[];
  shapes: readonly string[];
  devices: readonly string[];
  countries: readonly string[];
  q: string;
  range: DateRange;
}) {
  return infiniteQueryOptions({
    queryKey: adminKeys.talentSearchesList(
      `${selectionKey(filter.surfaces)}|${selectionKey(filter.outcomes)}` +
        `|${selectionKey(filter.shapes)}|${selectionKey(filter.devices)}` +
        `|${selectionKey(filter.countries)}|${filter.q}` +
        `|${dateRangeKey(filter.range)}`,
    ),
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      fetchSearches(pageParam, {
        surfaces: filter.surfaces,
        outcomes: filter.outcomes,
        shapes: filter.shapes,
        devices: filter.devices,
        countries: filter.countries,
        q: filter.q || undefined,
        range: filter.range,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? null,
    staleTime: 30 * 1000,
  });
}

/** The Offers tab's keyset feed, newest first — same shape as the searches
 *  lane beside it. */
export function talentOffersQuery(filter: {
  recommended: readonly string[];
  currencies: readonly string[];
  range: DateRange;
}) {
  return infiniteQueryOptions({
    queryKey: adminKeys.talentOffersList(
      `${selectionKey(filter.recommended)}|${selectionKey(filter.currencies)}` +
        `|${dateRangeKey(filter.range)}`,
    ),
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      fetchOffers(pageParam, {
        recommended: filter.recommended,
        currencies: filter.currencies,
        range: filter.range,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? null,
    staleTime: 30 * 1000,
  });
}

/** The Declines tab's keyset feed, newest first — same shape as the offers lane
 *  beside it, and the same rule: per-record operational truth, never an aggregate. */
export function talentDeclinesQuery(filter: {
  reasons: readonly string[];
  who: readonly string[];
  replied: readonly string[];
  range: DateRange;
}) {
  return infiniteQueryOptions({
    queryKey: adminKeys.talentDeclinesList(
      `${selectionKey(filter.reasons)}|${selectionKey(filter.who)}|${selectionKey(filter.replied)}` +
        `|${dateRangeKey(filter.range)}`,
    ),
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      fetchDeclines(pageParam, {
        reasons: filter.reasons,
        who: filter.who,
        replied: filter.replied,
        range: filter.range,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? null,
    staleTime: 30 * 1000,
  });
}

/** The opt-out request queue. Short staleTime like the curated lists beside it:
 *  resolving a row changes it, and so does someone editing the suppression list. */
export const talentOptoutRequestsQuery = queryOptions({
  queryKey: adminKeys.talentOptoutRequests,
  queryFn: fetchOptoutRequests,
  staleTime: 30 * 1000,
});

/** The talent-preference review queue, keyset-paged. A short staleTime because it is a
 *  WORKLIST that two operators may share: a row somebody else just approved should stop
 *  being offered here.
 *
 *  `nextCursor` decides the end rather than a short page: the server mints it from a probe
 *  row, so a page that happens to be exactly full still says truthfully whether anything is
 *  behind it. */
export function sellerPreferencesQuery(status: string) {
  return infiniteQueryOptions({
    queryKey: adminKeys.sellerPreferences(status),
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      fetchSellerPreferences(status, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? null,
    staleTime: 15 * 1000,
  });
}

/** One search run's full detail (SCOUT's reasoning + its ranked picks). */
export function talentSearchQuery(id: string) {
  return queryOptions({
    queryKey: adminKeys.talentSearch(id),
    queryFn: () => fetchSearchDetail(id),
  });
}

// --- Developers: the agent debug dashboard -----------------------------------
//
// Page sizes are declared here and passed EXPLICITLY, so `getNextPageParam` can
// tell "a full page, there may be more" from "a short page, that was the end"
// without depending on a backend default it cannot see. Traces are immutable
// once written, so none of these refetch on focus: a turn that happened is not
// going to happen differently.

/** Turns arrive 50 at a time, runs 25. Projects are not cursor-paged (the
 *  distinct-project count is small), so one generous page covers it. */
export const DEBUG_TURNS_PAGE = 50;
export const DEBUG_FEED_PAGE = 25;
export const DEBUG_PROJECTS_PAGE = 200;

/** The turn scope the left rail lists: EVERY captured turn, one project, or the
 *  synthetic "No project" bucket (turns with no project at all). `all` is what a
 *  filter applied before a project is picked reads — the server narrows the whole
 *  store and only then cuts to a page, so the answer is not confined to whichever
 *  project happened to be open. */
export type DebugTurnScope =
  | { kind: "all" }
  | { kind: "project"; projectId: string }
  | { kind: "none" };

/** Stable cache-key string for a scope. */
function scopeKey(scope: DebugTurnScope): string {
  if (scope.kind === "all") return "__all__";
  return scope.kind === "none" ? "__no_project__" : scope.projectId;
}

/** Stable cache-key string for a multi-select selection. SORTED, so picking
 *  two values in either order hits one cached list rather than two identical
 *  ones. Shared by every server-filtered list key. */
function selectionKey(values: readonly string[]): string {
  return [...values].sort().join(",");
}

/** Stable cache-key string for an agent selection (see `selectionKey`). */
function agentsKey(agents: readonly string[]): string {
  return selectionKey(agents);
}

/** Projects with agent activity, searched server-side so the filter spans every
 *  project rather than the page already in memory. */
export function debugProjectsQuery(q: string) {
  return queryOptions({
    queryKey: adminKeys.debugProjects(q),
    queryFn: () => fetchDebugProjects({ q: q || undefined, limit: DEBUG_PROJECTS_PAGE }),
    staleTime: 30 * 1000,
  });
}

/** One scope's turns, newest first, keyset-paged by the oldest `ended_at`.
 *  `agents` narrows to turns in which at least one of them fired, `q` searches
 *  turn id / project / owner — both server-side, because the list is paged and
 *  narrowing the loaded page would hide every match further back. An empty array
 *  (or an empty `q`) is no filter. Under the `all` scope the same filters run
 *  over every captured turn rather than one project's. */
export function debugTurnsQuery(
  scope: DebugTurnScope,
  agents: readonly string[] = [],
  range: DateRange = EMPTY_DATE_RANGE,
  q = "",
) {
  return infiniteQueryOptions({
    queryKey: adminKeys.debugTurns(
      scopeKey(scope),
      `${agentsKey(agents)}|${dateRangeKey(range)}|${q}`,
    ),
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      fetchDebugTurns({
        projectId: scope.kind === "project" ? scope.projectId : undefined,
        noProject: scope.kind === "none" ? true : undefined,
        q: q || undefined,
        before: pageParam ?? undefined,
        limit: DEBUG_TURNS_PAGE,
        agents,
        range,
      }),
    initialPageParam: null as string | null,
    // A short page means the end. A full page means there MAY be more, so the
    // cursor is the oldest row's timestamp.
    getNextPageParam: (last) =>
      last.turns.length === DEBUG_TURNS_PAGE
        ? (last.turns[last.turns.length - 1]?.ended_at ?? null)
        : null,
    staleTime: 30 * 1000,
  });
}

/** Every agent's run within ONE turn. Immutable once written, so it never goes
 *  stale: the tabs can be flipped through without refetching. */
export function debugTurnQuery(turnId: string) {
  return queryOptions({
    queryKey: adminKeys.debugTurn(turnId),
    queryFn: () => fetchTurnRuns(turnId),
    staleTime: Infinity,
  });
}

/** One agent's runs across every turn, keyset-paged by the oldest `started_at`. */
export function debugAgentFeedQuery(agent: string) {
  return infiniteQueryOptions({
    queryKey: adminKeys.debugAgentFeed(agent),
    queryFn: ({ pageParam }: { pageParam: string | null }) => fetchAgentRuns(agent, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) =>
      last.runs.length === DEBUG_FEED_PAGE
        ? (last.runs[last.runs.length - 1]?.started_at ?? null)
        : null,
    staleTime: 30 * 1000,
  });
}
